import React, { useEffect, useRef, useState } from "react";
import { Glass, RegMarks } from "./Primitives";

/* Each card performs its own discipline, then holds still long
   enough to be read, then performs it again.

   The engineering card assembles from an exploded view, the mobile
   card reflows across a breakpoint, the motion card arrives through
   perspective on a curve it draws for you, and the AI card resolves
   itself out of binary. The loop only runs while the card is on
   screen, so nothing animates into an empty room. */

/* One cycle: perform, hold, retreat, repeat. */
const PERFORM = 2000; // the animation itself
const HOLD = 5200; // settled, so the copy can actually be read
const RETREAT = 900; // back to the start, quicker than the way in

const useInView = (threshold = 0.3) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Kept observing in both directions: the loop should stop when
    // the card leaves and pick up again when it returns.
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), {
      threshold,
    });

    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return [ref, inView];
};

const prefersReduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* Drives the perform / hold / retreat loop while `active`. */
const useCycle = (active) => {
  const [on, setOn] = useState(false);

  useEffect(() => {
    if (!active) return undefined;

    if (prefersReduced()) {
      setOn(true);
      return undefined;
    }

    let timer;
    let cancelled = false;

    const perform = () => {
      setOn(true);
      timer = setTimeout(() => {
        if (cancelled) return;
        setOn(false); // retreat
        timer = setTimeout(() => {
          if (!cancelled) perform();
        }, RETREAT);
      }, PERFORM + HOLD);
    };

    perform();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [active]);

  return on;
};

/* ---------- shared chrome ---------- */
const Tags = ({ tags }) => (
  <ul className="mt-auto flex flex-wrap gap-2 pt-8">
    {tags.map((t) => (
      <li
        key={t}
        className="mono rounded-full border border-rule-2 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] text-ink-70"
      >
        {t}
      </li>
    ))}
  </ul>
);

const Eyebrow = ({ tag, trailing }) => (
  <div className="flex items-baseline gap-5 pb-8">
    <span className="label text-ink">{tag}</span>
    <span className="h-px flex-1 bg-[var(--rule)]" />
    {trailing}
  </div>
);

const Title = ({ children }) => (
  <h3 className="text-[22px] md:text-[26px] font-medium tracking-[-0.03em] leading-[1.1]">
    {children}
  </h3>
);

const Body = ({ children }) => (
  <p className="pt-4 text-[15px] leading-[1.6] tracking-[-0.01em] text-ink-70">
    {children}
  </p>
);

const Pane = ({ children }) => (
  <Glass className="relative flex h-full flex-col rounded-[24px] p-8 md:p-10">
    <RegMarks inset={13} />
    {children}
  </Glass>
);

/* ============================================================
   1. ENGINEERING - an exploded view drawing itself together
   ============================================================ */
const PARTS = [
  { dx: "-30px", dy: "-16px", d: 0 },
  { dx: "34px", dy: "-10px", d: 130 },
  { dx: "-22px", dy: "26px", d: 260 },
  { dx: "26px", dy: "30px", d: 390 },
];

const Assemble = ({ tag, title, body, tags }) => {
  const [ref, inView] = useInView();
  const on = useCycle(inView);

  const part = (i) => ({
    "--dx": PARTS[i].dx,
    "--dy": PARTS[i].dy,
    "--pd": `${on ? PARTS[i].d : 0}ms`,
  });

  return (
    <div ref={ref} className={`pc-assemble h-full ${on ? "is-in" : ""}`}>
      <Pane>
        {/* Assembly guides: dashed leaders that retract once seated */}
        <span className="pc-guide pc-guide-a" />
        <span className="pc-guide pc-guide-b" />

        <div className="pc-part" style={part(0)}>
          <Eyebrow tag={tag} />
        </div>
        <div className="pc-part" style={part(1)}>
          <Title>{title}</Title>
        </div>
        <div className="pc-part" style={part(2)}>
          <Body>{body}</Body>
        </div>
        <div className="pc-part mt-auto" style={part(3)}>
          <Tags tags={tags} />
        </div>
      </Pane>
    </div>
  );
};

/* ============================================================
   2. MOBILE & DESKTOP - a breakpoint happening
   ============================================================ */
const NARROW = 390;
const WIDE = 1440;

const Reflow = ({ tag, title, body, tags }) => {
  const [ref, inView] = useInView();
  const on = useCycle(inView);
  const [w, setW] = useState(NARROW);
  const wRef = useRef(NARROW);

  useEffect(() => {
    if (prefersReduced()) {
      wRef.current = WIDE;
      setW(WIDE);
      return undefined;
    }

    const from = wRef.current;
    const to = on ? WIDE : NARROW;
    if (from === to) return undefined;

    const dur = on ? PERFORM : RETREAT;
    const t0 = performance.now();
    let raf;

    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const next = Math.round(from + (to - from) * eased);
      wRef.current = next;
      setW(next);
      if (p < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [on]);

  return (
    <div ref={ref} className={`pc-reflow h-full ${on ? "is-in" : ""}`}>
      <Pane>
        <Eyebrow
          tag={tag}
          trailing={
            <span className="label tabular-nums text-ink-45">{w}px</span>
          }
        />

        {/* The viewport edge, travelling as the layout widens */}
        <span className="pc-edge" aria-hidden="true" />

        <div className="pc-fluid">
          <Title>{title}</Title>
          <Body>{body}</Body>
        </div>

        <Tags tags={tags} />
      </Pane>
    </div>
  );
};

/* ============================================================
   3. MOTION & 3D - arrives through perspective, on a curve it
      draws for you
   ============================================================ */
const Tilt = ({ tag, title, body, tags }) => {
  const [ref, inView] = useInView();
  const on = useCycle(inView);

  return (
    <div ref={ref} className={`pc-tilt-wrap h-full ${on ? "is-in" : ""}`}>
      <div className="pc-tilt h-full">
        <Pane>
          <Eyebrow
            tag={tag}
            trailing={
              <svg
                className="pc-curve"
                viewBox="0 0 48 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M1 19 C 8 19, 12 1, 47 1"
                  stroke="currentColor"
                  strokeWidth="1"
                />
              </svg>
            }
          />
          <Title>{title}</Title>
          <Body>{body}</Body>
          <Tags tags={tags} />
        </Pane>
      </div>
    </div>
  );
};

/* ============================================================
   4. APPLIED AI - resolving out of binary
   ============================================================ */
const scrambleOf = (text) =>
  text.replace(/\S/g, () => (Math.random() < 0.5 ? "0" : "1"));

const Glitch = ({ tag, title, body, tags }) => {
  const [ref, inView] = useInView();
  const on = useCycle(inView);
  const [shown, setShown] = useState(() => scrambleOf(title));

  useEffect(() => {
    if (prefersReduced()) {
      setShown(title);
      return undefined;
    }

    // Retreating: fall back into noise rather than resolving.
    if (!on) {
      setShown(scrambleOf(title));
      return undefined;
    }

    const dur = PERFORM * 0.8;
    const t0 = performance.now();
    let raf;

    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const settled = Math.floor(p * title.length);

      let next = "";
      for (let i = 0; i < title.length; i++) {
        if (title[i] === " ") next += " ";
        else if (i < settled) next += title[i];
        else next += Math.random() < 0.5 ? "0" : "1";
      }

      setShown(next);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setShown(title);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [on, title]);

  return (
    <div ref={ref} className={`pc-glitch h-full ${on ? "is-in" : ""}`}>
      <Pane>
        <Eyebrow tag={tag} />
        <Title>
          <span className="pc-scramble">{shown}</span>
        </Title>
        <Body>{body}</Body>
        <Tags tags={tags} />
      </Pane>
    </div>
  );
};

const VARIANTS = {
  assemble: Assemble,
  reflow: Reflow,
  tilt: Tilt,
  glitch: Glitch,
};

const PracticeCard = ({ variant, ...rest }) => {
  const Cmp = VARIANTS[variant] || Assemble;
  return <Cmp {...rest} />;
};

export default PracticeCard;
